<template>
  <AdminLayout>
    <template #topbar-search>
      <div class="flex flex-column ">
        <IconField>
          <InputIcon class="pi pi-search" />
          <InputText v-model="searchValue" placeholder="Search by keywords..." size="small" />
        </IconField>
      </div>
    </template>
    <template #header>
      <Breadcrumb :items="items" />
      <Button label="Export" class="accent-button" @click="ExportRequests" />
    </template>
    <div v-if="currentView !== 'Verification Requests'" class="back-section">
      <Button icon="pi pi-arrow-left" label="Back" class="p-button-text Caption back-button mb-2" @click="goBack" />
    </div>
    <div class="container">
      <verificationRequests v-if="currentView === 'Verification Requests'" :tableData="tableData" :isLoading="isLoading"
        :rowsPerPage="rowsPerPage" :totalRecords="totalRecords" :currentPage="currentPage" @page-change="onPageChange"
        @view-profile="viewProfile" @row-click="viewTutorProfile" />
      <RequestDetails v-if="currentView === 'View request'" :tutorProfileDetails="tutorProfileDetails"
        @update-initialReview="initialReviewUpdate" @update-document="documentCheckUpdate"
        @update-background="backgrondCheckUpdate" @update-finalstatus="finalCheckUpdate" @approve-tutor="approveTutor"
        @reject-tutor="openRejectDialog" :loading="isLoading" />
    </div>
    <Dialog v-model:visible="showRejectDialog" header="Reject Tutor" modal :closable="false" style="width: 400px;">
      <div class="flex flex-column gap-3">
        <p>Please enter the reason for rejecting this tutor:</p>
        <Textarea v-model="messageContent" autoResize rows="3" placeholder="Enter rejection reason" class="w-full" />
      </div>
      <template #footer>
        <Button label="Cancel" severity="secondary" @click="closeRejectDialog" />
        <Button label="Reject" severity="danger" @click="confirmRejectTutor" :loading="isLoading" />
      </template>
    </Dialog>
  </AdminLayout>
</template>

<script setup>
import { ref, computed, onMounted,watch } from "vue";
import AdminLayout from "@/layout/AdminLayout.vue";
import verificationRequests from "@/components/dashboard/admin/verificationRequests/verificationRequests.vue";
import RequestDetails from "@/components/dashboard/admin/verificationRequests/requestDetails.vue";
import Breadcrumb from "@/components/CustomBreadcrumb.vue"
import Button from 'primevue/button';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import InputText from 'primevue/inputtext';
import { getVerificationRequests, getTutorProfileDetails, updateInitialReview, updateDocumentVerification, updateBackgroundCheck, updateFinalStatus, approveRequest, rejectRequest,exportVerificationRequests } from "@/api/admin";
import { useToast } from "primevue/usetoast";
import Dialog from 'primevue/dialog';
import Textarea from 'primevue/textarea';
import { notifySuccess } from '@/utils/notification';

const toast = useToast();
const currentView = ref('Verification Requests');
const tableData = ref([]);
const isLoading = ref(false);
const totalRecords = ref(0);
const rowsPerPage = ref(10);
const currentPage = ref(1);
const searchValue = ref("");
const tutorProfileDetails = ref(null);
const tutorID = ref("")
const messageContent = ref("");
const showRejectDialog = ref(false);

const fetchRequests = async () => {
  try {
    isLoading.value = true;
    const response = await getVerificationRequests({
      page: currentPage.value,
      limit: rowsPerPage.value,
      searchTerm: searchValue.value
    });

    tableData.value = response.data.data.tutors.map(tutor => ({
      ...tutor,
      teachingLanguage: Array.isArray(tutor.teachingLanguage)
        ? tutor.teachingLanguage.join(', ')
        : tutor.teachingLanguage,

    }));
    totalRecords.value = response.data.data.pagination.total;
  } catch (error) {
    console.error("Failed to fetch students:", error);
  } finally {
    isLoading.value = false;
  }
};
watch(searchValue, () => {
  currentPage.value = 1;
  fetchRequests();
});
onMounted(fetchRequests);

const items = computed(() => {
  const breadcrumbItems = [{ label: "Verification Requests" }];

  if (currentView.value === "View request") {
    breadcrumbItems.push({ label: tutorProfileDetails.value.username });
  }

  return breadcrumbItems;
});
const onPageChange = ({ page, limit }) => {
  currentPage.value = page;
  rowsPerPage.value = limit;
  fetchRequests();
};

const viewTutorProfile = async (rowData) => {
  try {
    tutorID.value = rowData._id
    const response = await getTutorProfileDetails(tutorID.value)
    tutorProfileDetails.value = response.data.data;
  } catch (err) {
    console.error("Unexpected error:", err);
  }
  currentView.value = "View request";
};

const updateFunctions = {
  initialReview: updateInitialReview,
  documentCheck: updateDocumentVerification,
  backgroundCheck: updateBackgroundCheck,
  finalCheck: updateFinalStatus,
};

const handleUpdate = async (type) => {
  try {
    await updateFunctions[type](tutorID.value);
  } catch (err) {
    console.error(`Unexpected error during update:`, err);
  }
};
const initialReviewUpdate = () => handleUpdate("initialReview");
const documentCheckUpdate = () => handleUpdate("documentCheck");
const backgrondCheckUpdate = () => handleUpdate("backgroundCheck");
const finalCheckUpdate = () => handleUpdate("finalCheck");

const approveTutor = async () => {
  try {
    isLoading.value = true;
    await approveRequest(tutorID.value);
    toast.add({
      severity: "success",
      summary: "Tutor Approved",
      detail: "The tutor has been successfully approved.",
      life: 3000
    });
  } catch (err) {
    isLoading.value = false;
    console.error("Unexpected error:", err);
  } finally {
    isLoading.value = false;
    currentView.value = "View request";
  }
};
const openRejectDialog = () => {
  showRejectDialog.value = true;
};

const closeRejectDialog = () => {
  showRejectDialog.value = false;
  messageContent.value = "";
};
const confirmRejectTutor = async () => {
  if (!messageContent.value) {
    toast.add({
      severity: "warn",
      summary: "Missing Reason",
      detail: "Please enter a reason before rejecting.",
      life: 3000,
    });
    return;
  }
  try {
    isLoading.value = true;
    await rejectRequest(tutorID.value, { messageContent: messageContent.value });
    isLoading.value = false;
    toast.add({
      severity: "success",
      summary: "Tutor Rejected",
      detail: "The tutor has been rejected.",
      life: 3000,
    });
  } catch (err) {
    isLoading.value = false;
    console.error("Unexpected error:", err);
  } finally {
    closeRejectDialog();
    currentView.value = "View request";
  }
};
const ExportRequests = async () => {
  try {
    const res = await exportVerificationRequests({ searchTerm: searchValue.value });
    const filePath = res.data.data.downloadLink;
    const link = document.createElement("a");
    link.href = filePath;
    link.download = "verification_Requests.csv";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    notifySuccess("Success!", "The file has been downloaded.");
  } catch (err) {
    console.error("Failed to export", err);
  }
}
const showList = () => {
  fetchRequests();
  currentView.value = 'Verification Requests';

};
const goBack = () => {
  showList();
};
</script>

<style>
.container {
  width: 100%;
  height: auto;
  border-radius: 8px;
}

.back-section {
  display: flex;
  align-items: center;
}

.back-button {
  color: var(--gray-600) !important;
  font-size: 12px !important;
}

.back-button:hover {
  color: var(--gray-600) !important;
  background-color: var(--gray-200) !important;
}

.back-button:active {
  color: var(--gray-600) !important;
}

.pi-arrow-left {
  color: var(--gray-600) !important;
  font-size: 12px !important;
}
</style>
